import { useCallback, useSyncExternalStore } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { domainEventBus, ROI_CONTOUR_EDIT_EVENT_TYPES } from '~/domain/events';
import type { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { directionLabel, oppositeDirection } from '~/utils/orientation';
import {
    calculateNormalizedOrientationValue,
    planePositionAlong,
} from '~/utils/units';

/**
 * A warning while `roi` lies partly outside its image, naming each side with
 * its first and last slice as a tile does; read again on every contour edit,
 * undo, redo and tool press or release.
 */
export function useStructurePastImage(
    roi: RegionOfInterest | undefined
): { text: string; detail: string } | undefined {
    const intl = useIntl();
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;
    const subscribe = useCallback(
        (onChange: () => void) => {
            // Read once every handler of the event has run: a structure
            // restores its pixels for an undo in its own handler, which may
            // come after this one.
            let subscribed = true;
            const read = () => queueMicrotask(() => subscribed && onChange());
            const subscriptions = ROI_CONTOUR_EDIT_EVENT_TYPES.map((type) =>
                domainEventBus.subscribe(type, read)
            );
            toolManager?.eventEmitter.on('toolStateChanged', read);
            return () => {
                subscribed = false;
                subscriptions.forEach((s) => s.unsubscribe());
                toolManager?.eventEmitter.off('toolStateChanged', read);
            };
        },
        [toolManager]
    );
    const pastImage = useSyncExternalStore(subscribe, () => roi?.pastImage);
    const image = roi?._imageGrid;
    if (!pastImage || !image) {
        return undefined;
    }
    const axes = [image.getUnitX(), image.getUnitY(), image.getUnitZ()];
    const sliceAt = (k: number) =>
        planePositionAlong(image.voxelCenterToWorldSpace([0, 0, k]), axes[2]);
    const { axis } = sliceAt(0);
    const sides = pastImage.faces.flatMap((slices, face) => {
        if (!slices) {
            return [];
        }
        const [from, to] = slices
            .map((k) => sliceAt(k).offsetMm)
            .toSorted((a, b) => a - b)
            .map((mm) => calculateNormalizedOrientationValue(mm, 1));
        const outward = directionLabel(axes[face >> 1]);
        const label = face % 2 ? outward : oppositeDirection(outward);
        const side = intl.formatMessage({
            id: `slice.numbering.direction.${label}`,
        });
        return intl.formatMessage(
            { id: 'structure.pastImage.side' },
            { side, axis, from, to, range: from !== to }
        );
    });
    const text = intl.formatMessage({ id: 'structure.pastImage' });
    return { text, detail: sides.join('; ') };
}
